import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// ITEM40-GROUP-CREATION-TEST: entrada isolada do modal de criacao de grupos.

const makeRequestId = () => {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
};

const participantNumber = option =>
  typeof option === "string" ? option.replace(/\D/g, "") : String(option?.number || "").replace(/\D/g, "");

const wait = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

const NewWhatsAppGroupModal = ({ open, onClose }) => {
  const { user } = useContext(AuthContext);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [whatsappId, setWhatsappId] = useState("");
  const [whatsapps, setWhatsapps] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [requestId, setRequestId] = useState(makeRequestId());

  useEffect(() => {
    if (!open) return;
    api.get("/whatsapp", { params: { companyId: user.companyId, session: 0 } })
      .then(({ data }) => {
        let allowed = data || [];
        if (user.profile !== "admin" && user.allowConnections !== "enabled") {
          const linkedIds = (user.whatsapps || []).map(item => Number(item.id));
          allowed = linkedIds.length
            ? allowed.filter(item => linkedIds.includes(Number(item.id)))
            : allowed.filter(item => Number(item.id) === Number(user.whatsappId));
        }
        setWhatsapps(allowed);
        const eligible = allowed.filter(item => item.status === "CONNECTED" && item.allowGroup && (
          item.channel === "whatsapp" ||
          (item.channel === "whatsapp_oficial" && item.phone_number_id)
        ));
        if (eligible.length === 1) setWhatsappId(eligible[0].id);
      })
      .catch(toastError);
  }, [open, user]);

  useEffect(() => {
    if (!open || search.trim().length < 3) return;
    const timer = setTimeout(() => {
      api.get("/contacts", { params: { searchParam: search.trim() } })
        .then(({ data }) => setOptions((data.contacts || []).filter(item => !item.isGroup)))
        .catch(toastError);
    }, 400);
    return () => clearTimeout(timer);
  }, [open, search]);

  const selectedConnection = useMemo(
    () => whatsapps.find(item => Number(item.id) === Number(whatsappId)),
    [whatsapps, whatsappId]
  );

  const resetAndClose = () => {
    setSubject("");
    setDescription("");
    setWhatsappId("");
    setParticipants([]);
    setOptions([]);
    setSearch("");
    setRequestId(makeRequestId());
    onClose();
  };

  const handleCreate = async () => {
    const numbers = Array.from(new Set(participants.map(participantNumber).filter(Boolean)));
    if (!subject.trim()) return toast.error("Informe o nome do grupo.");
    if (!selectedConnection) return toast.error("Selecione uma conexao.");
    if (selectedConnection.status !== "CONNECTED" || !selectedConnection.allowGroup) {
      return toast.error("A conexao precisa estar conectada e permitir grupos.");
    }
    if (selectedConnection.channel === "whatsapp_oficial" && !selectedConnection.phone_number_id) {
      return toast.error("A conexao oficial nao possui o identificador de telefone necessario.");
    }
    if (!numbers.length) return toast.error("Adicione pelo menos um participante.");
    if (selectedConnection.channel === "whatsapp_oficial" && numbers.length > 8) {
      return toast.error("A API Oficial permite no máximo 8 participantes no grupo.");
    }

    setLoading(true);
    try {
      const { data } = await api.post("/whatsapp-groups", {
        requestId,
        whatsappId: selectedConnection.id,
        subject: subject.trim(),
        description: description.trim(),
        participants: numbers
      });
      let operation = data;
      if (data.status === "pending_provider") {
        toast.info("A Meta recebeu a solicitação. Aguardando a confirmação do grupo...");
        for (let attempt = 0; attempt < 30 && operation.status === "pending_provider"; attempt += 1) {
          await wait(2000);
          const response = await api.get(`/whatsapp-groups/operations/${requestId}`);
          operation = response.data;
        }
        if (["failed", "failed_sync"].includes(operation.status)) {
          throw new Error(operation.error || "A Meta nao concluiu a criacao do grupo.");
        }
        if (operation.status !== "completed") {
          toast.info("O grupo continua sendo processado pela Meta e aparecerá após a confirmação.");
          resetAndClose();
          return;
        }
      }
      const warning = operation.warning || data.warning;
      toast.success(warning ? `Grupo criado. ${warning}` : "Grupo criado com sucesso.");
      if (operation.inviteLink) {
        window.prompt("Copie o link oficial e envie aos participantes para entrarem no grupo:", operation.inviteLink);
      }
      resetAndClose();
    } catch (error) {
      // Erro de validacao confirmado nao criou grupo. Uma nova chave permite
      // corrigir os dados; em 409/5xx a chave e mantida para impedir duplicacao.
      if (error?.response?.status === 400) setRequestId(makeRequestId());
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : resetAndClose} fullWidth maxWidth="sm">
      <DialogTitle>Criar grupo no WhatsApp</DialogTitle>
      <DialogContent>
        <TextField
          select
          fullWidth
          margin="dense"
          label="Conexao"
          value={whatsappId}
          onChange={event => setWhatsappId(event.target.value)}
        >
          {whatsapps.map(item => {
            const eligible = item.status === "CONNECTED" && item.allowGroup && (
              item.channel === "whatsapp" ||
              (item.channel === "whatsapp_oficial" && item.phone_number_id)
            );
            const reason = !item.allowGroup
              ? "grupos desativados"
              : item.status !== "CONNECTED"
                ? "desconectada"
                : item.channel === "whatsapp_oficial" && !item.phone_number_id
                  ? "ID de telefone ausente"
                  : "";
            return (
              <MenuItem key={item.id} value={item.id} disabled={!eligible}>
                {item.name}{reason ? ` - ${reason}` : ""}
              </MenuItem>
            );
          })}
        </TextField>
        <TextField
          fullWidth margin="dense" label="Nome do grupo" value={subject}
          inputProps={{ maxLength: 100 }} onChange={event => setSubject(event.target.value)}
        />
        <TextField
          fullWidth margin="dense" label="Descricao (opcional)" value={description}
          multiline rows={3} inputProps={{ maxLength: 2048 }}
          onChange={event => setDescription(event.target.value)}
        />
        <Autocomplete
          multiple freeSolo filterSelectedOptions
          options={options}
          value={participants}
          onChange={(_event, value) => setParticipants(value)}
          onInputChange={(_event, value) => setSearch(value)}
          getOptionLabel={option => typeof option === "string" ? option : `${option.name} - ${option.number}`}
          renderTags={(value, getTagProps) => value.map((option, index) => (
            <Chip {...getTagProps({ index })} key={`${participantNumber(option)}-${index}`}
              label={typeof option === "string" ? option : `${option.name} - ${option.number}`} />
          ))}
          renderInput={params => (
            <TextField {...params} margin="dense" label="Participantes"
              placeholder="Busque pelo nome ou digite DDI + DDD + numero" />
          )}
        />
        <Typography variant="caption" color="textSecondary">
          {selectedConnection?.channel === "whatsapp_oficial"
            ? "Na API Oficial, a Meta exige entrada por convite. Após criar, copie o link e envie aos participantes (máximo 8)."
            : "Números digitados são adicionados ao grupo; nenhum contato individual é criado automaticamente."}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} disabled={loading}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={handleCreate} disabled={loading}>
          {loading ? <CircularProgress size={22} /> : "Criar grupo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewWhatsAppGroupModal;
